import { Injector } from '@angular/core';
import {
	HttpClient,
	HttpHeaders,
	HttpErrorResponse,
	HttpHandler,
	HttpXhrBackend,
} from '@angular/common/http';

import { Observable } from 'rxjs/Observable';
import { throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { MetaData, UrlBuilder } from './../classes/urlBuilder';
import { IErrorDescription, ErrorSeverity, ErrorCategory } from '@ibank-globals/error';
import { AppSettings } from '../../app/app.settings';

declare var dtrum: any;

// XhrFactory interface for Angular 16+ compatibility
interface XhrFactory {
	build(): XMLHttpRequest;
}

// Custom XhrFactory for compatibility
class CustomXhrFactory implements XhrFactory {
	build(): XMLHttpRequest {
		return new XMLHttpRequest();
	}
}

export class HttpService {
	private static http: HttpClient;

	private static systemError: IErrorDescription = {
		code: 'SYSTEM',
		desc: 'System error',
		sev: ErrorSeverity.Error,
		cat: ErrorCategory.Technical,
	};
	private static jsonError: IErrorDescription = {
		code: 'PARSING',
		desc: 'Could not parse response in json format',
		sev: ErrorSeverity.Error,
		cat: ErrorCategory.Technical,
	};

	/*
	 * Creates HttpClient with all required dependencies
	 */
	private static initialize() {
		const xhrFactory = new CustomXhrFactory();
		const httpHandler = new HttpXhrBackend(xhrFactory);
		HttpService.http = new HttpClient(httpHandler);
	}

	private static ensureInitialized() {
		if (HttpService.http) return;
		HttpService.initialize();
	}

	private static createAntiForgeryHeader(headers: HttpHeaders, value: string): HttpHeaders {
		return headers.append('_af', value);
	}

	private static createAuthorizationHeader(headers: HttpHeaders, token: string): HttpHeaders {
		return headers.append('Authorization', 'bearer ' + token);
	}

	public static post<T>(
		actionUrl: string,
		data: any,
		wrapRequest: boolean = true
	): Observable<T> {
		HttpService.ensureInitialized();
		let metaData = new MetaData();

		let headers = new HttpHeaders();
		headers = this.createAntiForgeryHeader(headers, metaData.get('_af'));
		headers = HttpService.appendSandboxHeader(headers);

		let wrappedReq: any;
		let url = metaData.getContextPath(actionUrl);

		if (wrapRequest) {
			wrappedReq = metaData.wrap2Request(data);
		} else {
			wrappedReq = data;
		}

		let res = HttpService.http.post(url, wrappedReq, {
			headers: headers,
			withCredentials: true,
			responseType: 'text' as 'json',
		});

		return res
			.pipe(
				catchError(HttpService.serverError),
				map(HttpService.verifyJson),
				map(HttpService.resolve)
			);
	}

	private static appendSandboxHeader(headers: HttpHeaders): HttpHeaders {
		if (AppSettings.sandboxId) {
			return headers.append('sandbox_id', AppSettings.sandboxId);
		}
		return headers;
	}

	public static get<T>(actionUrl: string): Observable<T> {
		HttpService.ensureInitialized();

		let metaData = new MetaData();
		let url = metaData.getContextPath(actionUrl);

		let res = HttpService.http.get(url, {
			responseType: 'text' as 'json',
		});

		return res
			.pipe(
				catchError(HttpService.serverError),
				map(HttpService.verifyJson),
				map(HttpService.resolve)
			);
	}

	private static handleHttpError(error: any) {
		switch (error.status) {
			case 302:
				var input = error.url;
				var match = 'returnUrl=';

				var redirectUrl = input.substring(
					input.indexOf(match) + match.length,
					input.length
				);

				if (confirm('Redirecting to ' + redirectUrl)) {
					window.location.href = redirectUrl;
				}

				break;
			default:
				break;
		}
	}

	private static throwApiError(error: IErrorDescription) {
		throw error;
	}

	private static serverError(error: HttpErrorResponse) {
		HttpService.handleHttpError(error);
		HttpService.throwApiError({
			code: 'SYSTEM',
			desc: error.message,
			sev: ErrorSeverity.Error,
			cat: ErrorCategory.Technical,
		});
		return throwError(error);
	}

	private static setDynatraceEvent(eventName: string): void {
		if (!(window as any).hasOwnProperty('dtrum')) return;
		let eventVal: any = dtrum.enterAction(eventName);
		dtrum.leaveAction(eventVal);
	}

	/*
	 * Verifies that the response is in json format.
	 * If it is, return it,
	 * if not handle it accordingly
	 */
	private static verifyJson<T>(response: any): any {
		if (typeof response === 'string') {
			// Try JSON parse first — with responseType 'text', all responses arrive as strings
			try {
				return JSON.parse(response);
			} catch (e) {
				// Not valid JSON — handle as HTML response
				if (response.indexOf('redirectUriLocation') != -1) {
					let tempDiv = document.createElement('div');
					tempDiv.innerHTML = response;
					var url: any = tempDiv.querySelector('#redirectUriLocation');
					if (url) {
						url = url.value;
						HttpService.setDynatraceEvent('Login_successful');
						HttpService.setDynatraceEvent('Login_Homepage_Loaded_successfully');
						window.location.replace(url);
					}
				} else if (response.indexOf('form') > -1) {
					HttpService.setDynatraceEvent('Login_successful');
					HttpService.setDynatraceEvent('Login_Homepage_Loaded_successfully');
					window.document.body.innerHTML = response;
					window.document.forms[0].submit();
				} else {
					HttpService.throwApiError(HttpService.jsonError);
				}
			}
		}
		return response;
	}

	private static resolve<T>(response: any): any {
		if (!response) return;

		if (response.exception) {
			let err: IErrorDescription = response.exception;
			HttpService.throwApiError(err);
		} else {
			return (response.payload as T) || response;
		}
	}
}
